import React, { useMemo } from "react";
import { View, StyleSheet, Text } from "react-native";
import { MotiView } from "moti";
import { Users, ChatCircle, Clock, Question } from "@phosphor-icons/react";

import { StatCard } from "../../../components/adminDashboard/AICompanion";
import { colors, spacing } from "../../../styles/design-system";

type Props = {
  analytics?: {
    data?: any[];
    topActiveUsers?: any[];
    questionBreakdown?: {
      assignment?: number;
      course?: number;
    };
  };
};

export default function FacultyStatsCards({ analytics }: Props) {
  const courses = analytics?.data ?? [];
  const totalUsers = analytics?.topActiveUsers?.length ?? 0;

  const totalSessions = useMemo(
    () => courses.reduce((sum: number, c: any) => sum + (c.totalSessions ?? 0), 0),
    [courses]
  );

  const avgSessionMinutes = useMemo(() => {
    if (!courses.length) return 0;
    const seconds = courses.reduce((sum: number, c: any) => sum + (c.averageDuration ?? 0), 0) / courses.length;
    return Math.round(seconds / 60);
  }, [courses]);

  const assignmentQuestions = analytics?.questionBreakdown?.assignment ?? 0;
  const courseQuestions = analytics?.questionBreakdown?.course ?? 0;

  const questionsAnswered = assignmentQuestions + courseQuestions;

  return (
    <MotiView
      from={{ opacity: 0, translateY: 20 }}
      animate={{ opacity: 1, translateY: 0 }}
      transition={{ type: "timing", duration: 500, delay: 100 }}
      style={styles.statsGrid}>
      <StatCard
        title="Total Users"
        value={totalUsers.toLocaleString()}
        change=""
        changeType="positive"
        icon={Users}
        color="blue"
      />

      <StatCard
        title="Total Sessions"
        value={totalSessions.toLocaleString()}
        change=""
        changeType="positive"
        icon={ChatCircle}
        color="purple"
      />

      <StatCard
        title="Avg. Session Length"
        value={`${avgSessionMinutes} min`}
        change=""
        changeType="positive"
        icon={Clock}
        color="green"
      />
      <StatCard
        title="Questions Answered"
        value={questionsAnswered.toLocaleString()}
        change=""
        changeType="positive"
        icon={Question}
        color="blue"
        footer={
          <View style={styles.footerInline}>
            <View style={styles.footerRow}>
              <Text style={styles.footerText}>{assignmentQuestions} assignment</Text>
            </View>

            <View style={styles.footerRow}>
              <Text style={styles.footerDot}>·</Text>
              <Text style={styles.footerText}>{courseQuestions} course</Text>
            </View>
          </View>
        }
      />
    </MotiView>
  );
}

const styles = StyleSheet.create({
  footerInline: {
    flexDirection: "row",
    flexWrap: "wrap",
    columnGap: 12,
    rowGap: 4,
    alignItems: "center",
  },
  footerRow: {
    flexDirection: "row",
    alignItems: "baseline",
  },

  footerText: {
    fontSize: 12,
    color: colors.grey[600],
  },

  footerDot: {
    fontSize: 12,
    color: colors.grey[400],
    marginHorizontal: 6,
    lineHeight: 12,
  },

  statsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.lg,
    marginBottom: spacing.xl,
    alignItems: "flex-start",
  },
});
